// Body for the `refinement-waiting-on-manufacturer` task, shared by:
//   - Ref - 4.   Waiting for Refinement design
//   - Ref - 4.1. Refinement design pre-approved
//
// The case is parked at Spark's side. The clinician's only job is to confirm
// receipt of the returned design — the forward edge (`refinement-design-received`)
// auto-renders below this body via TaskSurface and fires bare. The body is
// contextual: the latest design submission for THIS refinement + when it was sent.

import { useQuery } from '@tanstack/react-query';
import { ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { TaskBodyProps } from './index';
import { fetchTimeline } from '../../../services/timeline';
import type { TimelineEvent } from '../../patient-files/actions/types';
import { formatAbsolute, formatRelative, TIMELINE_HISTORY_STALE_MS } from '@odo/shared-ui';

interface Submission {
    id: string;
    createdAt: string;
    author: string;
    text: string;
    preApproved: boolean;
}

function extractSubmission(event: TimelineEvent, preApproved: boolean): Submission | null {
    const text = (event.payload as { designPlan?: string } | undefined)?.designPlan;
    if (!text || text.trim().length === 0) return null;
    return {
        id: event.id,
        createdAt: event.createdAt,
        author: event.createdByUser?.name ?? event.createdBy ?? 'Unknown',
        text,
        preApproved
    };
}

export function RefinementWaitingTask({ workCase: c }: TaskBodyProps) {
    const {
        data: latest,
        isLoading,
        isError,
        refetch,
        isFetching
    } = useQuery({
        queryKey: ['refinement-waiting-latest', c.id, c.phaseEnteredAt],
        queryFn: async (): Promise<Submission | null> => {
            const [submitRes, preApprovedRes] = await Promise.all([
                fetchTimeline(c.id, {
                    type: 'submit-refinement-for-design',
                    isLatest: true,
                    limit: 1
                }),
                fetchTimeline(c.id, {
                    type: 'refinement-design-pre-approved',
                    isLatest: true,
                    limit: 1
                })
            ]);
            const sinceMs = c.phaseEnteredAt ? new Date(c.phaseEnteredAt).getTime() : null;
            const candidates: Submission[] = [
                ...submitRes.data.map((e) => extractSubmission(e, false)),
                ...preApprovedRes.data.map((e) => extractSubmission(e, true))
            ]
                .filter((x): x is Submission => x !== null)
                .filter((x) => sinceMs === null || new Date(x.createdAt).getTime() >= sinceMs);
            if (candidates.length === 0) return null;
            const newestFirst = [...candidates].sort((a, b) =>
                b.createdAt.localeCompare(a.createdAt)
            );
            return newestFirst[0];
        },
        staleTime: TIMELINE_HISTORY_STALE_MS,
        enabled: Boolean(c.id)
    });

    return (
        <div className="flex-1 min-h-0 flex flex-col px-4 pt-3 pb-3 gap-3 font-inter">
            <div className="flex items-center gap-3 px-5 py-4 bg-blue-50 border border-blue-100 rounded-xl flex-shrink-0">
                <ClockIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900">
                        Waiting on the manufacturer
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                        Spark is working on this refinement. Once the manufacturer is done, advance
                        the case using the action below.
                    </div>
                </div>
            </div>

            {/* Latest submission card (scoped to this refinement) */}
            <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-2 flex-shrink-0 flex-wrap">
                    <span className="font-bold text-sm text-gray-800">Sent to manufacturer</span>
                    <div className="flex items-center gap-2">
                        {latest && (
                            <span className="text-[11px] text-gray-500 flex items-center gap-2">
                                <span className="truncate max-w-[140px]">{latest.author}</span>
                                <span className="text-gray-300">·</span>
                                <span
                                    title={formatAbsolute(latest.createdAt)}
                                    className="whitespace-nowrap"
                                >
                                    {formatRelative(latest.createdAt) ||
                                        formatAbsolute(latest.createdAt)}
                                </span>
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => refetch()}
                            disabled={isFetching}
                            className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md border border-gray-300 text-[11px] font-medium text-gray-500 bg-white hover:border-gray-400 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Refresh"
                        >
                            <ArrowPathIcon
                                className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`}
                            />
                            Refresh
                        </button>
                    </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-5 py-4">
                    {isLoading ? (
                        <div className="text-xs text-gray-400 italic">
                            Loading latest submission…
                        </div>
                    ) : isError ? (
                        <div className="text-xs text-red-500">
                            Failed to load latest submission.
                        </div>
                    ) : !latest ? (
                        <div className="text-xs text-gray-400 italic">
                            No refinement design submission found yet.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                                {latest.preApproved
                                    ? 'Refinement design · pre-approved'
                                    : 'Refinement design'}
                            </span>
                            <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                                {latest.text}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
